import { message } from 'antd'
import {
  applyMeasurementSystemToFormField,
  assetOverviewPermissionsCheck,
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import {
  FormFieldsByUniqueID,
  SimpleFormField,
} from 'components/form/formTypes'
import { GeckoToggleForm } from 'components/form/GeckoToggleForm'
import { useRolodexEdit } from 'features/fde'
import { useRolodexCache } from 'features/rolodex'
import React, { forwardRef, useMemo } from 'react'
import { SubmitHandler } from 'react-hook-form'
import {
  DamageCategory,
  DamageCategoryOptions,
  ExternalUserFieldAccess,
  GeckoEntryBase,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
  InputType,
  RolodexEntryPutJson,
} from 'types'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

interface EditableDamageCategoryFormProps {
  damageCategory: GeckoEntryBase<DamageCategory>
  measurementSystem: GeckoMeasurementSystem
  inEditMode: boolean
  inResolveMode: boolean
  forceRefresh: () => Promise<void>
  handleCancel?: () => void
  userPermissions: ReadonlySet<string> | undefined
}

/**
 * This form allows for the easy modification of Asset Models, returning the edited JSON to the callback (either onSave or onPreview)
 *
 * @param measurementSystem     The GeckoMeasureMentSystem currently being used by the user (for converting between metric and imperial)
 * @param damageCategory        The damageCategory that will be modified and returned
 * @param inEditMode            Boolean to determine if the form is in edit or read mode
 * @param forceRefresh          Callback function to force a refresh of the DamageCategory data
 * @param handleEditModeChange  Callback function when the edit mode changes
 * @param userPermissions       The user's permissions for the current site
 */
export const EditableDamageCategoryForm = forwardRef<
  HTMLFormElement,
  EditableDamageCategoryFormProps
>(
  (
    {
      measurementSystem,
      damageCategory,
      inEditMode,
      inResolveMode,
      forceRefresh,
      handleCancel,
      userPermissions,
    },
    ref
  ) => {
    const [messageApi, contextHolder] = message.useMessage()

    const DamageCategoryFormFields: FormFieldsByUniqueID[] = useMemo(() => {
      return [
        {
          uniqueID: damageCategory.id as string,
          formFields: [
            {
              label: 'Category',
              dataKey: 'category',
              value:
                damageCategory.data?.category ?? DamageCategoryOptions.UNKNOWN,
              isNeededForDerivedLayer: false,
              unit_magnitude: null,
              inputType: InputType.Dropdown,
              validationRule: optionalStringSchema,
              externalUserAccess: ExternalUserFieldAccess.ReadWrite,
              options: [
                {
                  label: DamageCategoryOptions.RUPTURE,
                  value: DamageCategoryOptions.RUPTURE,
                },
                {
                  label: 'Pinhole Leak',
                  value: DamageCategoryOptions.PINHOLE_LEAK,
                },
                {
                  label: DamageCategoryOptions.NONE,
                  value: DamageCategoryOptions.NONE,
                },
              ],
            },
            {
              label: 'Probability',
              dataKey: 'probability',
              value: damageCategory.data?.probability,
              isNeededForDerivedLayer: false,
              unit_magnitude: null,
              inputType: InputType.Number,
              validationRule: optionalNumberSchema,
              externalUserAccess: ExternalUserFieldAccess.ReadWrite,
            },
            {
              label: 'Consequence',
              dataKey: 'consequence',
              value: damageCategory.data?.consequence,
              isNeededForDerivedLayer: false,
              unit_magnitude: null,
              inputType: InputType.Number,
              validationRule: optionalNumberSchema,
              externalUserAccess: ExternalUserFieldAccess.ReadWrite,
            },
            {
              label: 'CR',
              dataKey: 'corrosionRate',
              value: damageCategory.data?.corrosionRate,
              isNeededForDerivedLayer: false,
              inputType: InputType.Number,
              unit_magnitude: GeckoMeasurementUnit.InchesPerYear,
              validationRule: optionalNumberSchema,
              externalUserAccess: ExternalUserFieldAccess.ReadWrite,
            },
          ],
        },
      ]
    }, [damageCategory])

    const { rolodexPatch } = useRolodexEdit()
    const { clearAll } = useRolodexCache()

    const handleSaveToRolodex = (entries: RolodexEntryPutJson[]) => {
      rolodexPatch(entries)
        .then(() => {
          if (handleCancel) {
            handleCancel()
          }
          messageApi.success('Successfully saved Damage Area to Rolodex!')
        })
        .catch(() => {
          messageApi.error(
            'Failed to update the Damage Area  in Rolodex! Please try again.'
          )
        })
        .finally(() => {
          clearAll()
          forceRefresh()
        })
    }

    const onSubmit: SubmitHandler<SimpleFormField> = (data) => {
      const convertedFormFields = createEditedObject(
        data,
        createUniqueFormFields(DamageCategoryFormFields)
      )

      const rolodexPatch = []
      const damageCategoryPatchBody = Object.assign({}, damageCategory)
      convertedFormFields[0].formFields.forEach((field) => {
        damageCategoryPatchBody.data[field.dataKey] = field.value
      })

      rolodexPatch.push(damageCategoryPatchBody)

      handleSaveToRolodex(rolodexPatch)
    }

    return (
      <>
        {contextHolder}
        {userPermissions && (
          <GeckoToggleForm
            formFieldsByUniqueID={DamageCategoryFormFields}
            inEditMode={inEditMode}
            inResolveMode={inResolveMode}
            permissionsCheck={{
              userPermissions: userPermissions,
              permissionsChecker: assetOverviewPermissionsCheck,
            }}
            measurementApplication={{
              measurementApplication: applyMeasurementSystemToFormField,
              measurementSystem: measurementSystem,
            }}
            onSubmit={onSubmit}
            ref={ref}
          />
        )}
      </>
    )
  }
)

EditableDamageCategoryForm.displayName = 'EditableDamageCategoryForm'
